// GuestErrorBoundary
//
// Wraps the guest-facing pages (checkin + guestapp). Without it, a React crash
// shows a blank white screen and we never know it happened. This boundary:
//   1. logs a `js_crash` guest tracking event (so the PM sees it in the timeline)
//   2. renders the shared ErrorPage instead of a blank screen
//
// reservationId is read from the URL so the crash can be attributed even if it
// happens before initGuestSession ran.

import React from 'react';
import ErrorPage from '../../ui/feedback/ErrorPage';
import { trackGuestError } from '../../utils/guestTracking';

// Extract the reservationId + surface from the current guest URL.
// /{slug}/checkin/{id} or /{slug}/guestbook/{id} (also /checkin/{id}, /guestbook/{id}).
const parseGuestUrl = () => {
  try {
    const path = window.location.pathname;
    const m = path.match(/\/(checkin|guestbook)\/([a-f0-9]{24})/i);
    if (!m) return { reservationId: null, surface: null };
    return { reservationId: m[2], surface: m[1] === 'checkin' ? 'checkin' : 'guestapp' };
  } catch {
    return { reservationId: null, surface: null };
  }
};

class GuestErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log to the guest tracking pipeline (fire-and-forget).
    const { reservationId, surface } = parseGuestUrl();
    if (reservationId) {
      trackGuestError(reservationId, surface, 'js_crash', error?.message || 'React render crash', {
        stack: (error?.stack || '').slice(0, 1000),
        componentStack: (info?.componentStack || '').slice(0, 1000),
      });
    }
    // Keep a console trace for dev.
    console.error('[GuestErrorBoundary] crash:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          code={500}
          title="Oups, une erreur est survenue"
          message="Veuillez recharger la page. Si le problème persiste, contactez votre hôte."
        />
      );
    }
    return this.props.children;
  }
}

export default GuestErrorBoundary;
